import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Clock, ArrowRight, ArrowLeft, Signpost, ClipboardCheck, Shield, Car } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import VisibleBreadcrumbs from '@/components/VisibleBreadcrumbs';
import RelatedPages from '@/components/RelatedPages';

const articles = [
  {
    id: 'pass-theory-first-time',
    icon: ClipboardCheck,
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    readTime: 5,
    title: {
      sv: 'Hur du klarar teoriprovet på första försöket',
      en: 'How to Pass the Theory Test on Your First Try',
      ar: 'كيف تنجح في امتحان التيوري من أول مرة',
      tr: 'Teori Sınavını İlk Seferde Nasıl Geçersiniz',
      fa: 'چگونه آزمون تئوری را از بار اول قبول شوید',
      so: 'Sida loo gudbiyaa imtixaanka markii ugu horreysay',
      de: 'Wie Sie die Theorieprüfung beim ersten Mal bestehen',
      es: 'Cómo aprobar el examen teórico a la primera',
      pl: 'Jak zdać egzamin teoretyczny za pierwszym razem',
    },
    summary: {
      sv: 'Praktiska tips och strategier för att förbereda dig och klara teoriprovet direkt. Lär dig de vanligaste fällorna och hur du undviker dem.',
      en: 'Practical tips and strategies to prepare and pass the theory test right away. Learn the most common pitfalls and how to avoid them.',
      ar: 'نصائح واستراتيجيات عملية للتحضير والنجاح في امتحان التيوري مباشرة. تعرّف على الأخطاء الشائعة وكيف تتجنبها.',
      tr: 'Teori sınavına hazırlanmak ve hemen geçmek için pratik ipuçları. En yaygın tuzakları ve nasıl kaçınılacağını öğrenin.',
      fa: 'نکات و استراتژی‌های عملی برای آمادگی و قبولی در آزمون تئوری. رایج‌ترین اشتباهات و نحوه اجتناب از آنها.',
      so: 'Talooyinka iyo xeeladaha diyaargarowga. Baro qalad-qalad-ka caadiga ah.',
      de: 'Praktische Tipps zur Vorbereitung. Lernen Sie die häufigsten Fallstricke kennen.',
      es: 'Consejos prácticos para prepararte y aprobar. Aprende los errores más comunes.',
      pl: 'Praktyczne wskazówki do przygotowania się. Poznaj najczęstsze pułapki.',
    },
    content: {
      sv: [
        { heading: '1. Börja med gratis övningsprov', text: 'Det bästa sättet att förbereda sig är att göra så många övningsprov som möjligt. DriveMaster erbjuder 33 övningsprov med 800+ frågor. Börja med det gratis provet för att se hur det fungerar.' },
        { heading: '2. Fokusera på trafikmärken', text: 'Ungefär 30% av frågorna handlar om trafikmärken. Lär dig alla 359 trafikmärken i vårt bibliotek. Börja med varningsskyltar och förbudsmärken - de är vanligast på provet.' },
        { heading: '3. Förstå reglerna, memorera inte', text: 'Istället för att memorera svar, försök förstå logiken bakom trafikreglerna. Våra teorilektioner förklarar varför reglerna finns.' },
        { heading: '4. Öva regelbundet', text: 'Gör minst 2-3 övningsprov per dag under 2 veckor innan provet. Regelbunden övning är bättre än att plugga allt på en gång.' },
        { heading: '5. Lär dig viktiga begrepp', text: 'Det finns 69 viktiga begrepp som ofta förekommer i provet. Lär dig dem på ditt eget språk med vår ordlista.' },
      ],
      en: [
        { heading: '1. Start with free practice tests', text: 'The best way to prepare is to take as many practice tests as possible. DriveMaster offers 33 tests with 800+ questions. Start with the free test to see how it works.' },
        { heading: '2. Focus on traffic signs', text: 'About 30% of questions are about traffic signs. Learn all 365 signs in our library. Start with warning and prohibition signs - they appear most on the exam.' },
        { heading: '3. Understand the rules, don\'t memorize', text: 'Instead of memorizing answers, understand the logic behind traffic rules. Our theory lessons explain why rules exist.' },
        { heading: '4. Practice regularly', text: 'Take at least 2-3 practice tests per day for 2 weeks before the exam. Regular practice is better than cramming.' },
        { heading: '5. Learn key terminology', text: 'There are 69 important terms that frequently appear. Learn them in your own language with our vocabulary list.' },
      ],
      ar: [
        { heading: '1. ابدأ بالامتحانات المجانية', text: 'أفضل طريقة للتحضير هي حل أكبر عدد ممكن من الامتحانات التجريبية. DriveMaster يوفر 33 امتحان مع أكثر من 800 سؤال. ابدأ بالامتحان المجاني لترى كيف يعمل.' },
        { heading: '2. ركّز على إشارات المرور', text: 'حوالي 30% من الأسئلة تتعلق بإشارات المرور. تعلّم كل الـ 359 إشارة في مكتبتنا. ابدأ بإشارات التحذير والمنع - هي الأكثر شيوعاً في الامتحان.' },
        { heading: '3. افهم القواعد، لا تحفظها', text: 'بدلاً من حفظ الإجابات، حاول فهم المنطق وراء قواعد المرور. دروسنا النظرية تشرح لماذا توجد هذه القواعد.' },
        { heading: '4. تدرّب بانتظام', text: 'حل على الأقل 2-3 تدريبات يومياً لمدة أسبوعين قبل اختبار المعرفة. التدريب المنتظم أفضل من الدراسة المكثفة.' },
        { heading: '5. تعلّم المصطلحات الأساسية', text: 'هناك 69 مصطلحاً مهماً يظهر كثيراً في الامتحان. تعلّمها بلغتك من خلال قائمة الكلمات الهامة.' },
      ],
      tr: [
        { heading: '1. Ücretsiz deneme sınavlarıyla başlayın', text: 'Hazırlanmanın en iyi yolu mümkün olduğunca çok deneme sınavı çözmektir. DriveMaster 800+ soru ile 24 sınav sunar.' },
        { heading: '2. Trafik işaretlerine odaklanın', text: 'Soruların yaklaşık %30\'u trafik işaretleriyle ilgilidir. 365 işaretin tamamını öğrenin.' },
        { heading: '3. Kuralları anlayın, ezberlemeyin', text: 'Cevapları ezberlemek yerine, trafik kurallarının arkasındaki mantığı anlayın.' },
        { heading: '4. Düzenli pratik yapın', text: 'Sınavdan 2 hafta önce günde en az 2-3 deneme sınavı çözün.' },
        { heading: '5. Temel terimleri öğrenin', text: 'Sınavda sık çıkan 69 önemli terim var. Bunları kendi dilinizde öğrenin.' },
      ],
      fa: [
        { heading: '1. با آزمون‌های رایگان شروع کنید', text: 'بهترین راه آمادگی حل هرچه بیشتر آزمون تمرینی است. DriveMaster با 800+ سوال، 33 آزمون ارائه می‌دهد.' },
        { heading: '2. روی تابلوها تمرکز کنید', text: 'حدود 30% سوالات درباره تابلوهای راهنمایی است. همه 365 تابلو را یاد بگیرید.' },
        { heading: '3. قوانین را بفهمید، حفظ نکنید', text: 'به جای حفظ کردن جواب‌ها، منطق پشت قوانین را درک کنید.' },
        { heading: '4. منظم تمرین کنید', text: '2 هفته قبل از آزمون، روزی حداقل 2-3 آزمون تمرینی حل کنید.' },
        { heading: '5. اصطلاحات کلیدی را بیاموزید', text: '69 اصطلاح مهم وجود دارد که زیاد در آزمون تکرار می‌شود.' },
      ],
      so: [
        { heading: '1. Ka bilow imtixaanada bilaashka ah', text: 'Habka ugu fiican ee diyaargarowgu waa inaad qaadato imtixaanno badan. DriveMaster waxay bixisaa 33 imtixaan.' },
        { heading: '2. Ka fiirso calaamadaha taraafikada', text: 'Ku dhowaad 30% su\'aalaha waxay ku saabsan yihiin calaamadaha. Baro dhammaan 359 calaamadood.' },
        { heading: '3. Faham xeerarka', text: 'Halkii aad ka xafiidisid jawaabaha, faham sababta xeerarka jiraan.' },
        { heading: '4. Si joogto ah u ku celceli', text: '2 toddobaad ka hor imtixaanka, maalin kasta ugu yaraan 2-3 imtixaan samee.' },
        { heading: '5. Baro ereyada muhiimka ah', text: 'Waxaa jira 69 eray oo muhiim ah oo soo noqnoqda imtixaanka.' },
      ],
      de: [
        { heading: '1. Beginnen Sie mit kostenlosen Übungstests', text: 'Der beste Weg sich vorzubereiten ist, so viele Tests wie möglich zu machen. DriveMaster bietet 33 Tests mit 800+ Fragen.' },
        { heading: '2. Konzentrieren Sie sich auf Verkehrszeichen', text: 'Etwa 30% der Fragen betreffen Verkehrszeichen. Lernen Sie alle 365 Zeichen.' },
        { heading: '3. Verstehen statt auswendig lernen', text: 'Verstehen Sie die Logik hinter den Regeln, anstatt Antworten auswendig zu lernen.' },
        { heading: '4. Regelmäßig üben', text: '2 Wochen vor der Prüfung täglich mindestens 2-3 Tests machen.' },
        { heading: '5. Wichtige Begriffe lernen', text: 'Es gibt 69 wichtige Begriffe, die häufig in der Prüfung vorkommen.' },
      ],
      es: [
        { heading: '1. Empieza con tests gratuitos', text: 'La mejor preparación es hacer tantos tests como sea posible. DriveMaster ofrece 33 tests con 800+ preguntas.' },
        { heading: '2. Enfócate en las señales', text: 'Aproximadamente el 30% de las preguntas son sobre señales. Aprende las 359 señales.' },
        { heading: '3. Comprende las reglas', text: 'En lugar de memorizar respuestas, entiende la lógica detrás de las normas de tráfico.' },
        { heading: '4. Practica regularmente', text: '2 semanas antes del examen, haz al menos 2-3 tests al día.' },
        { heading: '5. Aprende la terminología', text: 'Hay 69 términos importantes que aparecen frecuentemente en el examen.' },
      ],
      pl: [
        { heading: '1. Zacznij od darmowych testów', text: 'Najlepszym sposobem przygotowania jest rozwiązywanie jak największej liczby testów. DriveMaster oferuje 33 testów z 800+ pytaniami.' },
        { heading: '2. Skup się na znakach', text: 'Około 30% pytań dotyczy znaków drogowych. Naucz się wszystkich 359 znaków.' },
        { heading: '3. Rozumiej przepisy', text: 'Zamiast zapamiętywać odpowiedzi, zrozum logikę za przepisami ruchu drogowego.' },
        { heading: '4. Ćwicz regularnie', text: '2 tygodnie przed egzaminem rozwiązuj co najmniej 2-3 testy dziennie.' },
        { heading: '5. Naucz się terminologii', text: 'Jest 69 ważnych pojęć, które często pojawiają się na egzaminie.' },
      ],
    },
  },
  {
    id: 'top-traffic-signs',
    icon: Signpost,
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    readTime: 4,
    title: {
      sv: 'De 10 viktigaste trafikmärkena att kunna',
      en: 'The 10 Most Important Traffic Signs to Know',
      ar: 'أهم 10 إشارات مرور يجب أن تعرفها',
      tr: 'Bilmeniz Gereken En Önemli 10 Trafik İşareti',
      fa: '10 تابلوی مهم که باید بشناسید',
      so: '10 calaamadood oo muhiim ah',
      de: 'Die 10 wichtigsten Verkehrszeichen',
      es: 'Las 10 señales de tráfico más importantes',
      pl: '10 najważniejszych znaków drogowych',
    },
    summary: {
      sv: 'Dessa trafikmärken förekommer oftast i teoriprovet och i trafiken. Lär dig dem ordentligt för att öka dina chanser att klara provet.',
      en: 'These traffic signs appear most often in the theory test and on the road. Learn them well to increase your chances of passing.',
      ar: 'هذه الإشارات تظهر أكثر في امتحان التيوري وعلى الطريق. تعلّمها جيداً لزيادة فرصك بالنجاح.',
      tr: 'Bu trafik işaretleri sınavda ve trafikte en sık çıkar. İyi öğrenin.',
      fa: 'این تابلوها بیشترین تکرار را در آزمون دارند. خوب یادشان بگیرید.',
      so: 'Calaamadahaan waa kuwa ugu badan ee imtixaanka ku soo baxa.',
      de: 'Diese Verkehrszeichen kommen am häufigsten in der Prüfung vor.',
      es: 'Estas señales aparecen con más frecuencia en el examen.',
      pl: 'Te znaki pojawiają się najczęściej na egzaminie.',
    },
    content: {
      sv: [
        { heading: '1. Stoppskylt (B2)', text: 'Du MÅSTE stanna helt vid stopplinjen. Titta åt alla håll innan du kör vidare. En av de vanligaste skyltar att fråga om.' },
        { heading: '2. Väjningsplikt (B1)', text: 'Du måste lämna företräde åt fordon på den korsande vägen. Sänk farten och var beredd att stanna.' },
        { heading: '3. Hastighetsbegränsning', text: 'Olika hastighetsgränser gäller i tätort (50), landsväg (70-90) och motorväg (110-120). Alltid anpassad hastighet!' },
        { heading: '4. Huvudled (B4)', text: 'Du kör på en väg med företräde. Men kom ihåg - du kan fortfarande behöva väja vid speciella situationer.' },
        { heading: '5. Förbud mot omkörning (C28)', text: 'Du får inte köra om andra fordon. Gäller tills skylten upphävs.' },
      ],
      en: [
        { heading: '1. Stop Sign (B2)', text: 'You MUST come to a complete stop at the line. Look in all directions before proceeding. One of the most tested signs.' },
        { heading: '2. Give Way (B1)', text: 'You must yield to vehicles on the crossing road. Slow down and be prepared to stop.' },
        { heading: '3. Speed Limits', text: 'Different limits apply: urban (50), rural (70-90), highway (110-120). Always adapt your speed!' },
        { heading: '4. Priority Road (B4)', text: 'You are on a priority road. But remember, special situations may still require you to yield.' },
        { heading: '5. No Overtaking (C28)', text: 'You may not overtake other vehicles. Valid until the sign is cancelled.' },
      ],
      ar: [
        { heading: '1. إشارة التوقف (B2)', text: 'يجب أن تتوقف تماماً عند خط التوقف. انظر في كل الاتجاهات قبل المتابعة. من أكثر الإشارات التي تأتي في الامتحان.' },
        { heading: '2. أولوية المرور (B1)', text: 'يجب أن تعطي الأولوية للمركبات على الطريق المتقاطع. خفف السرعة وكن مستعداً للتوقف.' },
        { heading: '3. حدود السرعة', text: 'حدود مختلفة: داخل المدينة (50)، خارج المدينة (70-90)، الطريق السريع (110-120). دائماً اضبط سرعتك!' },
        { heading: '4. الطريق الرئيسي (B4)', text: 'أنت على طريق له الأولوية. لكن تذكر، بعض الحالات الخاصة قد تتطلب منك التوقف.' },
        { heading: '5. ممنوع التجاوز (C28)', text: 'لا يجوز تجاوز المركبات الأخرى. يسري حتى تُلغى الإشارة.' },
      ],
      tr: [
        { heading: '1. Dur İşareti (B2)', text: 'Çizgide tamamen durmalısınız. Tüm yönlere bakın. Sınavda en çok çıkan işaretlerden biridir.' },
        { heading: '2. Yol Ver (B1)', text: 'Kesişen yoldaki araçlara yol vermelisiniz. Yavaşlayın ve durmaya hazır olun.' },
        { heading: '3. Hız Sınırları', text: 'Farklı limitler: şehir içi (50), kırsal (70-90), otoyol (110-120). Her zaman hızınızı ayarlayın!' },
        { heading: '4. Ana Yol (B4)', text: 'Öncelikli bir yoldasınız. Ama özel durumlarda yol vermeniz gerekebilir.' },
        { heading: '5. Sollama Yasağı (C28)', text: 'Diğer araçları sollamak yasaktır. İşaret kaldırılana kadar geçerlidir.' },
      ],
      fa: [
        { heading: '1. تابلوی ایست (B2)', text: 'باید کاملاً توقف کنید. به همه جهات نگاه کنید. یکی از پرتکرارترین تابلوها در آزمون.' },
        { heading: '2. حق تقدم (B1)', text: 'باید به وسایل نقلیه در جاده تقاطع حق تقدم بدهید.' },
        { heading: '3. محدودیت سرعت', text: 'محدودیت‌های مختلف: شهر (50)، بین شهری (70-90)، بزرگراه (110-120).' },
        { heading: '4. جاده اصلی (B4)', text: 'در جاده با حق تقدم هستید. اما شرایط خاص ممکن است نیاز به توقف داشته باشد.' },
        { heading: '5. سبقت ممنوع (C28)', text: 'سبقت از وسایل نقلیه دیگر ممنوع است.' },
      ],
      so: [
        { heading: '1. Calaamada Joogso (B2)', text: 'Waa inaad si buuxda u joogsataa xariiqda. Dhinac kasta fiiri.' },
        { heading: '2. Dariiq-bixinta (B1)', text: 'Waa inaad meel ka dhigtaa gaadiidka wadada ka gudbaysa.' },
        { heading: '3. Xaddiga Xawaraaha', text: 'Xadad kala duwan: magaalo (50), miyiga (70-90), waddada dheer (110-120).' },
        { heading: '4. Jidka Ugu Weyn (B4)', text: 'Waxaad ku jirtaa waddo mudnaaneed leh.' },
        { heading: '5. Dhaafitaanka la mamnuucay (C28)', text: 'Gaadiid kale dhaafitaankooda waa mamnuuc.' },
      ],
      de: [
        { heading: '1. Stoppschild (B2)', text: 'Sie MÜSSEN an der Linie vollständig anhalten. Schauen Sie in alle Richtungen.' },
        { heading: '2. Vorfahrt gewähren (B1)', text: 'Sie müssen Fahrzeugen auf der kreuzenden Straße Vorfahrt gewähren.' },
        { heading: '3. Geschwindigkeitsbegrenzungen', text: 'Verschiedene Limits: Stadt (50), Landstraße (70-90), Autobahn (110-120).' },
        { heading: '4. Vorfahrtsstraße (B4)', text: 'Sie fahren auf einer Vorfahrtsstraße.' },
        { heading: '5. Überholverbot (C28)', text: 'Das Überholen anderer Fahrzeuge ist verboten.' },
      ],
      es: [
        { heading: '1. Señal de Stop (B2)', text: 'DEBE detenerse completamente en la línea. Mire en todas direcciones.' },
        { heading: '2. Ceda el paso (B1)', text: 'Debe ceder el paso a los vehículos de la vía transversal.' },
        { heading: '3. Límites de velocidad', text: 'Diferentes límites: urbano (50), rural (70-90), autopista (110-120).' },
        { heading: '4. Vía prioritaria (B4)', text: 'Está en una vía con prioridad.' },
        { heading: '5. Prohibido adelantar (C28)', text: 'No puede adelantar a otros vehículos.' },
      ],
      pl: [
        { heading: '1. Znak Stop (B2)', text: 'MUSISZ się całkowicie zatrzymać na linii. Sprawdź wszystkie kierunki.' },
        { heading: '2. Ustąp pierwszeństwa (B1)', text: 'Musisz ustąpić pierwszeństwa pojazdom na drodze krzyżującej się.' },
        { heading: '3. Ograniczenia prędkości', text: 'Różne limity: miasto (50), droga krajowa (70-90), autostrada (110-120).' },
        { heading: '4. Droga z pierwszeństwem (B4)', text: 'Jesteś na drodze z pierwszeństwem przejazdu.' },
        { heading: '5. Zakaz wyprzedzania (C28)', text: 'Nie wolno wyprzedzać innych pojazdów.' },
      ],
    },
  },
  {
    id: 'exam-day-tips',
    icon: Shield,
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    readTime: 3,
    title: {
      sv: 'Vad du bör veta inför provdagen',
      en: 'What You Should Know Before Exam Day',
      ar: 'ما يجب معرفته قبل يوم الامتحان',
      tr: 'Sınav Günü Öncesi Bilinmesi Gerekenler',
      fa: 'آنچه باید قبل از روز آزمون بدانید',
      so: 'Waxa aad u baahan tahay inaad ogaato ka hor maalinta imtixaanka',
      de: 'Was Sie vor dem Prüfungstag wissen sollten',
      es: 'Lo que debes saber antes del día del examen',
      pl: 'Co powinieneś wiedzieć przed dniem egzaminu',
    },
    summary: {
      sv: 'Allt du behöver veta om vad du ska ta med, hur lång tid provet tar, och vad som händer om du inte klarar det.',
      en: 'Everything you need to know about what to bring, how long the test takes, and what happens if you don\'t pass.',
      ar: 'كل ما تحتاج معرفته: ماذا تحضر معك، كم يستغرق الامتحان، وماذا يحدث لو لم تنجح.',
      tr: 'Bilmeniz gereken her şey: ne getirmeniz gerektiği, sınav süresi ve başarısız olursanız ne olur.',
      fa: 'همه چیزهایی که باید بدانید: چه بیاورید، مدت آزمون، و اگر قبول نشدید.',
      so: 'Wax walba oo aad u baahan tahay inaad ogaato.',
      de: 'Alles was Sie wissen müssen: was mitbringen, Dauer, und bei Nichtbestehen.',
      es: 'Todo lo que necesitas saber: qué llevar, duración y si no apruebas.',
      pl: 'Wszystko co musisz wiedzieć: co zabrać, czas trwania i co jeśli nie zdasz.',
    },
    content: {
      sv: [
        { heading: 'Vad ska du ta med?', text: 'Ta med giltig legitimation (ID-kort eller pass). Du behöver inget annat - penna och papper finns på plats.' },
        { heading: 'Hur lång tid tar provet?', text: 'Du har 50 minuter för 70 frågor (65 räknas). Det är ungefär 43 sekunder per fråga, men de flesta klarar det snabbare.' },
        { heading: 'Godkänt resultat', text: 'Du måste svara rätt på minst 52 av 65 frågor (80%) för att bli godkänd. Det finns inga minuspoäng.' },
        { heading: 'Om du inte klarar det', text: 'Du kan göra om provet efter en vecka. Använd tiden till att öva mer med DriveMaster!' },
      ],
      en: [
        { heading: 'What to bring?', text: 'Bring valid ID (ID card or passport). You don\'t need anything else.' },
        { heading: 'How long is the test?', text: 'You have 50 minutes for 70 questions (65 are scored). That\'s about 43 seconds per question.' },
        { heading: 'Passing score', text: 'You must answer at least 52 of 65 questions correctly (80%) to pass. No negative marking.' },
        { heading: 'If you don\'t pass', text: 'You can retake the test after one week. Use the time to practice more!' },
      ],
      ar: [
        { heading: 'ماذا تحضر معك؟', text: 'أحضر هوية سارية (بطاقة هوية أو جواز سفر). لا تحتاج شيئاً آخر.' },
        { heading: 'كم يستغرق الامتحان؟', text: 'لديك 50 دقيقة لـ 70 سؤالاً (يُحسب منها 65). حوالي 43 ثانية لكل سؤال.' },
        { heading: 'درجة النجاح', text: 'يجب الإجابة صحيحاً على 52 من 65 سؤال (80%) للنجاح. لا توجد درجات سالبة.' },
        { heading: 'إذا لم تنجح', text: 'يمكنك إعادة الامتحان بعد أسبوع. استغل الوقت للتدريب أكثر مع DriveMaster!' },
      ],
      tr: [
        { heading: 'Ne getirmelisiniz?', text: 'Geçerli kimlik (kimlik kartı veya pasaport) getirin.' },
        { heading: 'Sınav ne kadar sürer?', text: '70 soru (65\'i puanlanır) için 50 dakikanız var. Soru başına yaklaşık 43 saniye.' },
        { heading: 'Geçme notu', text: '65 sorudan en az 52\'sini doğru cevaplamalısınız (%80). Eksi puan yok.' },
        { heading: 'Geçemezseniz', text: 'Bir hafta sonra tekrar girebilirsiniz.' },
      ],
      fa: [
        { heading: 'چه بیاورید؟', text: 'شناسنامه معتبر (کارت شناسایی یا پاسپورت) بیاورید.' },
        { heading: 'مدت آزمون؟', text: '50 دقیقه برای 70 سوال (65 سوال امتیاز دارد). حدود 43 ثانیه برای هر سوال.' },
        { heading: 'نمره قبولی', text: 'باید حداقل 52 از 65 سوال را درست جواب دهید (80%).' },
        { heading: 'اگر قبول نشدید', text: 'بعد از یک هفته می‌توانید دوباره شرکت کنید.' },
      ],
      so: [
        { heading: 'Maxaad la timaadaa?', text: 'Aqoonsiga (kaarka aqoonsiga ama baasaboorka).' },
        { heading: 'Muddo intee le\'eg?', text: '50 daqiiqo 70 su\'aalood (65 ayaa la xisaabiyaa). Qiyaastii 43 ilbiriqsi su\'aal kasta.' },
        { heading: 'Dhibcaha guusha', text: 'Waa inaad si sax ah ugu jawaabta ugu yaraan 52 ka mid ah 65 (80%).' },
        { heading: 'Haddii aadan gudbiin', text: 'Toddobaad ka dib ayaad dib u qaadan kartaa.' },
      ],
      de: [
        { heading: 'Was mitbringen?', text: 'Gültigen Ausweis (Personalausweis oder Reisepass) mitbringen.' },
        { heading: 'Wie lange dauert die Prüfung?', text: '50 Minuten für 70 Fragen (65 werden gewertet). Etwa 43 Sekunden pro Frage.' },
        { heading: 'Bestehensgrenze', text: 'Mindestens 52 von 65 Fragen richtig (80%). Keine Minuspunkte.' },
        { heading: 'Bei Nichtbestehen', text: 'Nach einer Woche Wiederholung möglich.' },
      ],
      es: [
        { heading: '¿Qué llevar?', text: 'Identificación válida (DNI o pasaporte).' },
        { heading: '¿Cuánto dura el examen?', text: '50 minutos para 70 preguntas (65 puntúan). Unos 43 segundos por pregunta.' },
        { heading: 'Nota para aprobar', text: 'Al menos 52 de 65 preguntas correctas (80%). Sin puntos negativos.' },
        { heading: 'Si no apruebas', text: 'Puedes repetir después de una semana.' },
      ],
      pl: [
        { heading: 'Co zabrać?', text: 'Ważny dokument tożsamości (dowód osobisty lub paszport).' },
        { heading: 'Jak długo trwa egzamin?', text: '50 minut na 70 pytań (65 punktowanych). Około 43 sekund na pytanie.' },
        { heading: 'Próg zdawalności', text: 'Minimum 52 z 65 poprawnych odpowiedzi (80%). Brak punktów ujemnych.' },
        { heading: 'Jeśli nie zdasz', text: 'Możesz podejść ponownie po tygodniu.' },
      ],
    },
  },
  {
    id: 'speed-limits-sweden',
    icon: Car,
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    readTime: 4,
    title: {
      sv: 'Hastighetsgränser i Sverige – Komplett Guide',
      en: 'Speed Limits in Sweden – Complete Guide',
      ar: 'حدود السرعة في السويد – دليل شامل',
      tr: 'İsveç\'te Hız Sınırları – Eksiksiz Rehber',
      fa: 'محدودیت سرعت در سوئد – راهنمای کامل',
      so: 'Xaddiga Xawaaraha Iswiidhan – Hagitaan Buuxa',
      de: 'Geschwindigkeitsbegrenzungen in Schweden – Vollständiger Leitfaden',
      es: 'Límites de Velocidad en Suecia – Guía Completa',
      pl: 'Limity Prędkości w Szwecji – Kompletny Przewodnik',
    },
    summary: {
      sv: 'Allt om hastighetsgränser i Sverige: tätort, landsväg, motorväg. När du ska sänka farten och vad som händer vid fortkörning.',
      en: 'Everything about speed limits in Sweden: urban areas, rural roads, highways. When to slow down and penalties for speeding.',
      ar: 'كل شيء عن حدود السرعة في السويد: داخل المدينة، خارج المدينة، الطريق السريع. متى تخفف السرعة وعقوبات السرعة الزائدة.',
      tr: 'İsveç\'te hız sınırları hakkında her şey: şehir içi, kırsal yollar, otoyollar.',
      fa: 'همه چیز درباره محدودیت سرعت در سوئد: شهری، بین‌شهری، بزرگراه.',
      so: 'Wax walba oo ku saabsan xaddiga xawaaraha Iswiidhan.',
      de: 'Alles über Geschwindigkeitsbegrenzungen in Schweden: Stadt, Land, Autobahn.',
      es: 'Todo sobre los límites de velocidad en Suecia: ciudad, carretera, autopista.',
      pl: 'Wszystko o limitach prędkości w Szwecji: miasto, droga krajowa, autostrada.',
    },
    content: {
      sv: [
        { heading: 'Tätort – 30, 40 eller 50 km/h', text: 'I tätbebyggt område gäller normalt 50 km/h om inget annat anges. Vid skolor och bostadsområden kan det vara 30 km/h. Anpassa alltid hastigheten efter förhållandena!' },
        { heading: 'Landsväg – 70 eller 90 km/h', text: 'Utanför tätort gäller normalt 70 km/h. På bättre landsvägar kan det vara 90 km/h. Var uppmärksam på viltvarningsskyltar!' },
        { heading: 'Motorväg – 110 eller 120 km/h', text: 'På motorväg gäller 110 km/h normalt, men på vissa sträckor 120 km/h. Håll säkerhetsavståndet – minst 3 sekunders regel.' },
        { heading: 'Anpassad hastighet', text: 'Oavsett skyltad hastighet MÅSTE du anpassa din hastighet efter väder, sikt, väglag och trafiksituation. Det här är en av de vanligaste frågorna på teoriprovet.' },
        { heading: 'Straff vid fortkörning', text: 'Böterna börjar på 2000 kr och ökar med hastigheten. Vid 31+ km/h för fort kan du förlora körkortet. Rattfylleri + fortkörning kan ge fängelse.' },
      ],
      en: [
        { heading: 'Urban areas – 30, 40 or 50 km/h', text: 'In built-up areas, the default limit is 50 km/h unless otherwise posted. Near schools and residential areas, it may be 30 km/h. Always adapt your speed!' },
        { heading: 'Rural roads – 70 or 90 km/h', text: 'Outside built-up areas, the default is 70 km/h. On better rural roads, it may be 90 km/h. Watch for wildlife warning signs!' },
        { heading: 'Highways – 110 or 120 km/h', text: 'On motorways, the limit is usually 110 km/h, but some stretches allow 120 km/h. Keep safe distance – at least 3-second rule.' },
        { heading: 'Adapted speed', text: 'Regardless of posted limits, you MUST adapt speed to weather, visibility, road conditions and traffic. This is one of the most common theory test questions.' },
        { heading: 'Speeding penalties', text: 'Fines start at 2000 SEK and increase with speed. At 31+ km/h over, you may lose your license.' },
      ],
      ar: [
        { heading: 'داخل المدينة – 30، 40 أو 50 كم/ساعة', text: 'في المناطق السكنية، الحد الطبيعي 50 كم/ساعة ما لم يُحدد غير ذلك. بالقرب من المدارس قد يكون 30 كم/ساعة. دائماً اضبط سرعتك!' },
        { heading: 'خارج المدينة – 70 أو 90 كم/ساعة', text: 'خارج المناطق السكنية، الحد الطبيعي 70 كم/ساعة. على الطرق الأفضل قد يكون 90 كم/ساعة. انتبه لإشارات الحيوانات البرية!' },
        { heading: 'الطريق السريع – 110 أو 120 كم/ساعة', text: 'على الطريق السريع عادة 110 كم/ساعة، وبعض المقاطع 120 كم/ساعة. حافظ على مسافة الأمان – قاعدة 3 ثوانٍ على الأقل.' },
        { heading: 'السرعة المناسبة', text: 'بغض النظر عن السرعة المحددة، يجب أن تضبط سرعتك حسب الطقس والرؤية وحالة الطريق والمرور. هذا من أكثر أسئلة التيوري شيوعاً.' },
        { heading: 'عقوبات السرعة الزائدة', text: 'الغرامات تبدأ من 2000 كرونة وتزداد. عند 31+ كم/ساعة فوق الحد قد تفقد رخصتك.' },
      ],
      tr: [
        { heading: 'Şehir içi – 30, 40 veya 50 km/s', text: 'Yerleşim bölgelerinde varsayılan hız 50 km/s. Okul yakınlarında 30 km/s olabilir.' },
        { heading: 'Kırsal yollar – 70 veya 90 km/s', text: 'Yerleşim dışında varsayılan hız 70 km/s. Daha iyi yollarda 90 km/s olabilir.' },
        { heading: 'Otoyol – 110 veya 120 km/s', text: 'Otoyollarda genelde 110 km/s, bazı bölgelerde 120 km/s.' },
        { heading: 'Uygun hız', text: 'Hava koşullarına, görüşe ve trafiğe göre hızınızı ayarlamalısınız.' },
        { heading: 'Hız cezaları', text: 'Cezalar 2000 SEK\'ten başlar. 31+ km/s fazlada ehliyet kaybı olabilir.' },
      ],
      fa: [
        { heading: 'شهری – 30، 40 یا 50 کیلومتر', text: 'در مناطق مسکونی حد پیش‌فرض 50 کیلومتر است. نزدیک مدارس ممکن است 30 باشد.' },
        { heading: 'بین‌شهری – 70 یا 90 کیلومتر', text: 'خارج از شهر حد پیش‌فرض 70 کیلومتر است.' },
        { heading: 'بزرگراه – 110 یا 120 کیلومتر', text: 'در بزرگراه‌ها معمولاً 110، برخی مسیرها 120 کیلومتر.' },
        { heading: 'سرعت مناسب', text: 'باید سرعت را با شرایط آب و هوا و جاده تنظیم کنید.' },
        { heading: 'جریمه سرعت', text: 'جریمه از 2000 کرون شروع می‌شود.' },
      ],
      so: [
        { heading: 'Magaalada – 30, 40 ama 50 km/s', text: 'Aagga degaanka xaddiga caadiga ah waa 50 km/s.' },
        { heading: 'Wadooyinka miyiga – 70 ama 90 km/s', text: 'Magaalada ka baxsan xaddiga waa 70 km/s.' },
        { heading: 'Waddada dheer – 110 ama 120 km/s', text: 'Wadooyinka dheer xaddigu waa 110 km/s.' },
        { heading: 'Xawaaraha ku habboon', text: 'Waa inaad la qabsataa cimilada iyo xaaladda jidka.' },
        { heading: 'Ciqaabta xawaaraha', text: 'Ganaaxyadu waxay ka bilaabmaan 2000 SEK.' },
      ],
      de: [
        { heading: 'Innerorts – 30, 40 oder 50 km/h', text: 'In geschlossenen Ortschaften gilt standardmäßig 50 km/h. In Schulnähe kann es 30 km/h sein.' },
        { heading: 'Landstraße – 70 oder 90 km/h', text: 'Außerhalb geschlossener Ortschaften gilt 70 km/h. Auf besseren Straßen 90 km/h.' },
        { heading: 'Autobahn – 110 oder 120 km/h', text: 'Auf Autobahnen gilt 110 km/h, einige Abschnitte 120 km/h.' },
        { heading: 'Angepasste Geschwindigkeit', text: 'Sie müssen die Geschwindigkeit an Wetter und Straßenverhältnisse anpassen.' },
        { heading: 'Strafen bei Geschwindigkeitsüberschreitung', text: 'Bußgelder ab 2000 SEK. Bei 31+ km/h Entzug des Führerscheins möglich.' },
      ],
      es: [
        { heading: 'Zona urbana – 30, 40 o 50 km/h', text: 'En zonas edificadas, el límite por defecto es 50 km/h. Cerca de colegios puede ser 30 km/h.' },
        { heading: 'Carretera – 70 o 90 km/h', text: 'Fuera de zona urbana, el límite es 70 km/h. En mejores carreteras puede ser 90 km/h.' },
        { heading: 'Autopista – 110 o 120 km/h', text: 'En autopistas, 110 km/h normalmente, algunos tramos 120 km/h.' },
        { heading: 'Velocidad adaptada', text: 'Debe adaptar la velocidad a las condiciones meteorológicas y de tráfico.' },
        { heading: 'Multas por exceso de velocidad', text: 'Multas desde 2000 SEK. A 31+ km/h de exceso, posible pérdida del carnet.' },
      ],
      pl: [
        { heading: 'Teren zabudowany – 30, 40 lub 50 km/h', text: 'W terenie zabudowanym domyślny limit to 50 km/h. Przy szkołach może być 30 km/h.' },
        { heading: 'Droga krajowa – 70 lub 90 km/h', text: 'Poza terenem zabudowanym limit to 70 km/h. Na lepszych drogach 90 km/h.' },
        { heading: 'Autostrada – 110 lub 120 km/h', text: 'Na autostradach 110 km/h, niektóre odcinki 120 km/h.' },
        { heading: 'Dostosowana prędkość', text: 'Musisz dostosować prędkość do warunków pogodowych i drogowych.' },
        { heading: 'Kary za przekroczenie', text: 'Mandaty od 2000 SEK. Przy 31+ km/h powyżej limitu możliwa utrata prawa jazdy.' },
      ],
    },
  },
  {
    id: 'winter-driving-sweden',
    icon: Shield,
    color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    readTime: 5,
    title: {
      sv: 'Vinterkörning i Sverige – Allt du behöver veta',
      en: 'Winter Driving in Sweden – Everything You Need to Know',
      ar: 'القيادة الشتوية في السويد – كل ما تحتاج معرفته',
      tr: 'İsveç\'te Kış Sürüşü – Bilmeniz Gereken Her Şey',
      fa: 'رانندگی زمستانی در سوئد – همه چیزهایی که باید بدانید',
      so: 'Darawalnimada Jiilaalka Iswiidhan',
      de: 'Winterfahren in Schweden – Alles was Sie wissen müssen',
      es: 'Conducción Invernal en Suecia – Todo lo que Necesitas Saber',
      pl: 'Jazda Zimą w Szwecji – Wszystko co Musisz Wiedzieć',
    },
    summary: {
      sv: 'Vinterdäck, halkbekämpning, dimljus och anpassad hastighet. Lär dig reglerna för säker vinterkörning i Sverige.',
      en: 'Winter tires, anti-skid measures, fog lights and adapted speed. Learn the rules for safe winter driving in Sweden.',
      ar: 'إطارات الشتاء، مكافحة الانزلاق، أضواء الضباب والسرعة المناسبة. تعلّم قواعد القيادة الآمنة في الشتاء بالسويد.',
      tr: 'Kış lastikleri, buzlanma, sis farları ve uygun hız. İsveç\'te güvenli kış sürüşü kuralları.',
      fa: 'لاستیک زمستانی، مبارزه با لغزندگی، چراغ مه و سرعت مناسب.',
      so: 'Taayaraha jiilaalka, ka hortagga simbiriirixda, laydhadhka ceeryaanka.',
      de: 'Winterreifen, Glättebekämpfung, Nebelscheinwerfer und angepasste Geschwindigkeit.',
      es: 'Neumáticos de invierno, antideslizante, luces de niebla y velocidad adaptada.',
      pl: 'Opony zimowe, środki przeciwpoślizgowe, światła przeciwmgielne i dostosowana prędkość.',
    },
    content: {
      sv: [
        { heading: 'Vinterdäck – obligatoriskt', text: 'Från 1 december till 31 mars MÅSTE du ha vinterdäck om det är vinterväglag. Mönsterdjupet måste vara minst 3 mm. Dubbdäck är tillåtna 1 oktober – 15 april.' },
        { heading: 'Anpassad hastighet i halka', text: 'På isig eller snöig väg ska du sänka farten rejält. Bromsförmågan kan vara 10 gånger sämre på is jämfört med torr asfalt. Öka avståndet till framförvarande bil.' },
        { heading: 'Dimljus och belysning', text: 'Använd dimljus bak vid kraftigt snöfall eller dimma. Halvljus ska alltid vara tänt. Helljus är bra på mörka landsvägar men bländar mötande.' },
        { heading: 'Halkbekämpning', text: 'Om bilen börjar slira: ta foten från gasen, trampa INTE på bromsen, styra i den riktning du vill åka. Bromsar med ABS? Trampa fast och håll – systemet pulsar åt dig.' },
        { heading: 'Förberedelser', text: 'Ha alltid med: isskrapa, filt, starthjälpskablar och ficklampa. Kontrollera att kylvätska och spolarvätska tål kyla. Rensa snö och is från hela bilen innan körning.' },
      ],
      en: [
        { heading: 'Winter tires – mandatory', text: 'From December 1 to March 31, winter tires are mandatory in winter conditions. Minimum tread depth: 3 mm. Studded tires allowed October 1 – April 15.' },
        { heading: 'Speed in icy conditions', text: 'On icy or snowy roads, reduce speed significantly. Braking distance can be 10x longer on ice vs dry asphalt. Increase following distance.' },
        { heading: 'Fog lights and lighting', text: 'Use rear fog light in heavy snow or fog. Dipped beam must always be on. High beam is good on dark rural roads but blinds oncoming traffic.' },
        { heading: 'Skid control', text: 'If the car starts to skid: release gas, do NOT brake, steer in the direction you want to go. With ABS brakes? Press firmly and hold.' },
        { heading: 'Preparations', text: 'Always carry: ice scraper, blanket, jump cables and flashlight. Check coolant and washer fluid. Clear all snow and ice before driving.' },
      ],
      ar: [
        { heading: 'إطارات الشتاء – إلزامية', text: 'من 1 ديسمبر إلى 31 مارس يجب استخدام إطارات شتوية في ظروف الشتاء. الحد الأدنى لعمق النقش: 3 ملم. الإطارات المسمارية مسموحة من 1 أكتوبر – 15 أبريل.' },
        { heading: 'السرعة على الجليد', text: 'على الطرق الجليدية أو الثلجية، خفف السرعة كثيراً. مسافة الفرملة قد تكون 10 أضعاف على الجليد مقارنة بالأسفلت الجاف. زد المسافة عن السيارة الأمامية.' },
        { heading: 'أضواء الضباب والإنارة', text: 'استخدم ضوء الضباب الخلفي في الثلج الكثيف أو الضباب. الأنوار المنخفضة يجب أن تكون مشتعلة دائماً.' },
        { heading: 'التحكم بالانزلاق', text: 'إذا بدأت السيارة بالانزلاق: ارفع قدمك عن الغاز، لا تفرمل، وجّه المقود باتجاه حركتك المطلوبة. مع ABS؟ اضغط بقوة واستمر.' },
        { heading: 'التحضيرات', text: 'احمل دائماً: كاشطة الجليد، بطانية، كوابل شحن وكشاف. نظّف كل الثلج والجليد قبل القيادة.' },
      ],
      tr: [
        { heading: 'Kış lastikleri – zorunlu', text: '1 Aralık – 31 Mart arası kış koşullarında kış lastiği zorunludur. Minimum diş derinliği: 3 mm.' },
        { heading: 'Buzda hız', text: 'Buzlu yollarda hızı ciddi şekilde azaltın. Fren mesafesi 10 kat uzayabilir.' },
        { heading: 'Sis farları', text: 'Yoğun kar veya siste arka sis farını kullanın. Kısa far her zaman açık olmalıdır.' },
        { heading: 'Kayma kontrolü', text: 'Araç kaymaya başlarsa: gazdan çekin, fren YAPMAYIN, gitmek istediğiniz yöne direksiyon kırın.' },
        { heading: 'Hazırlıklar', text: 'Her zaman yanınızda bulundurun: buz kazıyıcı, battaniye, takviye kablosu.' },
      ],
      fa: [
        { heading: 'لاستیک زمستانی – اجباری', text: 'از 1 دسامبر تا 31 مارس لاستیک زمستانی الزامی است. حداقل عمق آج: 3 میلیمتر.' },
        { heading: 'سرعت در یخبندان', text: 'در جاده‌های یخ‌زده سرعت را خیلی کم کنید. مسافت ترمز ممکن است 10 برابر شود.' },
        { heading: 'چراغ مه', text: 'در برف سنگین یا مه از چراغ مه عقب استفاده کنید.' },
        { heading: 'کنترل لغزش', text: 'اگر ماشین شروع به لغزش کرد: گاز را رها کنید، ترمز نزنید، فرمان را به سمت مطلوب بچرخانید.' },
        { heading: 'آمادگی‌ها', text: 'همیشه همراه داشته باشید: یخ‌تراش، پتو، کابل باتری.' },
      ],
      so: [
        { heading: 'Taayaraha jiilaalka – waajib', text: 'Diseembar 1 ilaa Maarso 31 taayaraha jiilaalku waa waajib.' },
        { heading: 'Xawaaraha barafka', text: 'Wadooyinka barafaysan xawaaraha si weyn u yaree.' },
        { heading: 'Laydhadhka ceeryaanka', text: 'Baraf badan ama ceeryaan ka isticmaal laydhadhka ceeryaanka dambe.' },
        { heading: 'Xakamaynta simbiriirixda', text: 'Haddii gaadhigu simbiriirixo: gaaska iska daa, birreeg ha tumin.' },
        { heading: 'Diyaargarow', text: 'Mar walba wado: xarrashaad baraf, buste, iyo xadhig batari.' },
      ],
      de: [
        { heading: 'Winterreifen – Pflicht', text: 'Vom 1. Dezember bis 31. März sind Winterreifen bei winterlichen Bedingungen Pflicht. Mindestprofiltiefe: 3 mm.' },
        { heading: 'Geschwindigkeit bei Glätte', text: 'Auf eisigen Straßen Geschwindigkeit deutlich reduzieren. Bremsweg kann 10x länger sein.' },
        { heading: 'Nebelscheinwerfer', text: 'Bei starkem Schneefall oder Nebel Nebelschlussleuchte verwenden.' },
        { heading: 'Schleuderkontrolle', text: 'Bei Schleudern: Gas wegnehmen, NICHT bremsen, in gewünschte Richtung lenken.' },
        { heading: 'Vorbereitung', text: 'Immer dabei haben: Eiskratzer, Decke, Starthilfekabel, Taschenlampe.' },
      ],
      es: [
        { heading: 'Neumáticos de invierno – obligatorios', text: 'Del 1 de diciembre al 31 de marzo son obligatorios en condiciones invernales. Profundidad mínima: 3 mm.' },
        { heading: 'Velocidad en hielo', text: 'En carreteras heladas, reduzca mucho la velocidad. La distancia de frenado puede ser 10 veces mayor.' },
        { heading: 'Luces antiniebla', text: 'Use luz antiniebla trasera con nieve intensa o niebla.' },
        { heading: 'Control de derrape', text: 'Si el coche derrapa: suelte el acelerador, NO frene, gire hacia donde quiere ir.' },
        { heading: 'Preparación', text: 'Lleve siempre: rascador de hielo, manta, cables de arranque.' },
      ],
      pl: [
        { heading: 'Opony zimowe – obowiązkowe', text: 'Od 1 grudnia do 31 marca opony zimowe są obowiązkowe w warunkach zimowych. Minimalna głębokość bieżnika: 3 mm.' },
        { heading: 'Prędkość na lodzie', text: 'Na oblodzonych drogach znacznie zmniejsz prędkość. Droga hamowania może być 10 razy dłuższa.' },
        { heading: 'Światła przeciwmgielne', text: 'Przy silnym śniegu lub mgle użyj tylnych świateł przeciwmgielnych.' },
        { heading: 'Kontrola poślizgu', text: 'Jeśli auto się ślizga: zdejmij nogę z gazu, NIE hamuj, kieruj w pożądanym kierunku.' },
        { heading: 'Przygotowanie', text: 'Zawsze miej: skrobaczkę, koc, kable rozruchowe, latarkę.' },
      ],
    },
  },
  {
    id: 'right-of-way-rules',
    icon: Signpost,
    color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
    readTime: 4,
    title: {
      sv: 'Väjningsregler och högerregeln i Sverige',
      en: 'Right of Way Rules in Sweden',
      ar: 'قواعد الأولوية في المرور السويدي',
      tr: 'İsveç\'te Yol Hakkı Kuralları',
      fa: 'قوانین حق تقدم در سوئد',
      so: 'Xeerarka Mudnaanta Jidka Iswiidhan',
      de: 'Vorfahrtsregeln in Schweden',
      es: 'Reglas de Prioridad en Suecia',
      pl: 'Zasady Pierwszeństwa w Szwecji',
    },
    summary: {
      sv: 'Högerregeln, väjningsplikt och cirkulationsplatser. De viktigaste reglerna om företräde som du MÅSTE kunna på teoriprovet.',
      en: 'Right-hand rule, give way obligations and roundabouts. The most important priority rules you MUST know for the theory test.',
      ar: 'قاعدة اليمين، واجب التنحي والدوارات. أهم قواعد الأولوية التي يجب معرفتها لامتحان التيوري.',
      tr: 'Sağ kuralı, yol verme zorunluluğu ve kavşaklar. Sınav için bilmeniz gereken kurallar.',
      fa: 'قانون راست، تکلیف حق تقدم و میدان‌ها. مهمترین قوانین اولویت برای آزمون.',
      so: 'Xeerka midig, waajibka dariiq-bixinta. Xeerarka ugu muhiimsan.',
      de: 'Rechts-vor-links-Regel, Vorfahrt und Kreisverkehre. Die wichtigsten Vorfahrtsregeln.',
      es: 'Regla de la derecha, ceda el paso y rotondas. Las reglas de prioridad más importantes.',
      pl: 'Zasada prawej ręki, ustępowanie i ronda. Najważniejsze zasady pierwszeństwa.',
    },
    content: {
      sv: [
        { heading: 'Högerregeln', text: 'I en korsning utan skyltar eller signaler gäller högerregeln: du ska lämna företräde åt fordon som kommer från höger. Det här gäller INTE på huvudleder eller utfarter.' },
        { heading: 'Väjningsplikt (skylt B1)', text: 'Väjningspliktskylten betyder att du MÅSTE lämna företräde åt all trafik på den korsande vägen. Sänk farten och var beredd att stanna.' },
        { heading: 'Stopplikt (skylt B2)', text: 'Du MÅSTE stanna helt, även om det inte finns någon trafik. Stanna vid stopplinjen eller vid korsningen.' },
        { heading: 'Cirkulationsplats (rondell)', text: 'Fordon i cirkulationsplatsen har företräde. Du ska lämna företräde innan du kör in. Signalera alltid när du lämnar rondellen.' },
        { heading: 'Utfart', text: 'Om du kör ut från en parkeringsplats, tankstation eller liknande har ALL annan trafik företräde – fotgängare, cyklister och bilar.' },
      ],
      en: [
        { heading: 'Right-hand rule', text: 'At intersections without signs or signals, the right-hand rule applies: yield to vehicles coming from the right. Does NOT apply on priority roads or exits.' },
        { heading: 'Give way sign (B1)', text: 'The give way sign means you MUST yield to all traffic on the crossing road. Slow down and be ready to stop.' },
        { heading: 'Stop sign (B2)', text: 'You MUST come to a complete stop, even if there is no traffic. Stop at the line or at the intersection.' },
        { heading: 'Roundabouts', text: 'Vehicles in the roundabout have priority. You must yield before entering. Always signal when exiting.' },
        { heading: 'Exits', text: 'When exiting a parking lot, gas station or similar, ALL other traffic has priority – pedestrians, cyclists and cars.' },
      ],
      ar: [
        { heading: 'قاعدة اليمين', text: 'في التقاطعات بدون إشارات، تطبق قاعدة اليمين: أعط الأولوية للقادم من اليمين. لا تطبق على الطرق الرئيسية.' },
        { heading: 'إشارة التنحي (B1)', text: 'إشارة التنحي تعني أنك يجب أن تعطي الأولوية لكل المرور على الطريق المتقاطع. خفف السرعة واستعد للتوقف.' },
        { heading: 'إشارة التوقف (B2)', text: 'يجب أن تتوقف تماماً حتى لو لم يكن هناك مرور. توقف عند الخط أو عند التقاطع.' },
        { heading: 'الدوار (الروتاري)', text: 'المركبات في الدوار لها الأولوية. يجب أن تعطي الأولوية قبل الدخول. دائماً أشر عند الخروج.' },
        { heading: 'المخارج', text: 'عند الخروج من موقف سيارات أو محطة بنزين، كل المرور الآخر له الأولوية – المشاة، الدراجات والسيارات.' },
      ],
      tr: [
        { heading: 'Sağ kuralı', text: 'İşaret olmayan kavşaklarda sağdan gelen araca yol verin.' },
        { heading: 'Yol ver işareti (B1)', text: 'Kesişen yoldaki tüm trafiğe yol vermelisiniz.' },
        { heading: 'Dur işareti (B2)', text: 'Trafik olmasa bile tamamen durmalısınız.' },
        { heading: 'Kavşaklar (döner kavşak)', text: 'Kavşaktaki araçların önceliği vardır. Çıkarken sinyal verin.' },
        { heading: 'Çıkışlar', text: 'Otopark veya benzin istasyonundan çıkarken tüm trafiğin önceliği vardır.' },
      ],
      fa: [
        { heading: 'قانون راست', text: 'در تقاطع‌های بدون تابلو، به وسایل نقلیه از سمت راست حق تقدم بدهید.' },
        { heading: 'تابلوی حق تقدم (B1)', text: 'باید به تمام ترافیک جاده متقاطع حق تقدم بدهید.' },
        { heading: 'تابلوی ایست (B2)', text: 'حتی اگر ترافیکی نیست باید کاملاً توقف کنید.' },
        { heading: 'میدان‌ها', text: 'وسایل نقلیه در میدان حق تقدم دارند. هنگام خروج حتماً راهنما بزنید.' },
        { heading: 'خروجی‌ها', text: 'هنگام خروج از پارکینگ یا پمپ بنزین همه حق تقدم دارند.' },
      ],
      so: [
        { heading: 'Xeerka midig', text: 'Isgoyska aan calaamad lahayn, mudnaanta sii gaadiidka midigta kaa imanaya.' },
        { heading: 'Calaamada dariiq-bixinta (B1)', text: 'Waa inaad mudnaanta siisaa dhammaan taraafikada wadada ka gudbaysa.' },
        { heading: 'Calaamada joogso (B2)', text: 'Waa inaad si buuxda u joogsataa xitaa haddii taraafiig aanu jirin.' },
        { heading: 'Isgoyska wareega', text: 'Gaadiidka isgoyska ku jira wuu mudnaanyahay. Calaamee markaad ka baxayso.' },
        { heading: 'Meesha laga baxo', text: 'Markaad ka baxayso baarkinka dhammaan taraafikada kale wuu mudnaanyahay.' },
      ],
      de: [
        { heading: 'Rechts-vor-links', text: 'An Kreuzungen ohne Schilder gilt: Vorfahrt für Fahrzeuge von rechts.' },
        { heading: 'Vorfahrt gewähren (B1)', text: 'Sie müssen allen Fahrzeugen auf der kreuzenden Straße Vorfahrt gewähren.' },
        { heading: 'Stoppschild (B2)', text: 'Sie müssen vollständig anhalten, auch wenn kein Verkehr ist.' },
        { heading: 'Kreisverkehr', text: 'Fahrzeuge im Kreisverkehr haben Vorfahrt. Beim Verlassen blinken.' },
        { heading: 'Ausfahrten', text: 'Beim Verlassen von Parkplätzen hat aller anderer Verkehr Vorfahrt.' },
      ],
      es: [
        { heading: 'Regla de la derecha', text: 'En cruces sin señales, ceda el paso a vehículos que vienen por la derecha.' },
        { heading: 'Ceda el paso (B1)', text: 'Debe ceder el paso a todo el tráfico de la vía transversal.' },
        { heading: 'Señal de stop (B2)', text: 'Debe detenerse completamente, aunque no haya tráfico.' },
        { heading: 'Rotondas', text: 'Los vehículos en la rotonda tienen prioridad. Señalice al salir.' },
        { heading: 'Salidas', text: 'Al salir de un aparcamiento, todo el tráfico tiene prioridad.' },
      ],
      pl: [
        { heading: 'Zasada prawej ręki', text: 'Na skrzyżowaniach bez znaków ustąp pojazdom z prawej strony.' },
        { heading: 'Ustąp pierwszeństwa (B1)', text: 'Musisz ustąpić wszystkim pojazdom na drodze krzyżującej się.' },
        { heading: 'Znak stop (B2)', text: 'Musisz się całkowicie zatrzymać, nawet bez ruchu.' },
        { heading: 'Ronda', text: 'Pojazdy na rondzie mają pierwszeństwo. Sygnalizuj przy wyjeździe.' },
        { heading: 'Wyjazdy', text: 'Wyjeżdżając z parkingu, cały ruch ma pierwszeństwo.' },
      ],
    },
  },
];

const pageTexts = {
  pageTitle: { sv: 'Artiklar & Tips', en: 'Articles & Tips', ar: 'مقالات ونصائح', tr: 'Makaleler & İpuçları', fa: 'مقالات و نکات', so: 'Maqaallada & Talooyinka', de: 'Artikel & Tipps', es: 'Artículos y Consejos', pl: 'Artykuły i Porady' },
  pageSubtitle: { sv: 'Allt du behöver veta för att klara teoriprovet', en: 'Everything you need to know to pass the theory test', ar: 'كل ما تحتاج معرفته للنجاح في امتحان التيوري', tr: 'Teori sınavını geçmek için bilmeniz gereken her şey', fa: 'همه چیزهایی که برای قبولی باید بدانید', so: 'Wax walba oo aad u baahan tahay', de: 'Alles was Sie für die Theorieprüfung wissen müssen', es: 'Todo lo que necesitas para aprobar', pl: 'Wszystko co musisz wiedzieć o egzaminie' },
  readMore: { sv: 'Läs mer', en: 'Read more', ar: 'اقرأ المزيد', tr: 'Devamını oku', fa: 'بیشتر بخوانید', so: 'Akhri wax badan', de: 'Weiterlesen', es: 'Leer más', pl: 'Czytaj więcej' },
  readTime: { sv: 'min läsning', en: 'min read', ar: 'دقائق قراءة', tr: 'dk okuma', fa: 'دقیقه مطالعه', so: 'daqiiqo akhris', de: 'Min. Lesezeit', es: 'min lectura', pl: 'min czytania' },
  backToArticles: { sv: 'Tillbaka till artiklar', en: 'Back to articles', ar: 'العودة للمقالات', tr: 'Makalelere dön', fa: 'بازگشت به مقالات', so: 'Ku noqo maqaallada', de: 'Zurück zu Artikeln', es: 'Volver a artículos', pl: 'Wróć do artykułów' },
};

const articlePaths = {
  sv: '/artiklar', en: '/en/articles', ar: '/ar/maqalat', tr: '/tr/makaleler',
  fa: '/fa/maqalat', so: '/so/maqaallada', de: '/de/artikel', es: '/es/articulos', pl: '/pl/artykuly'
};

export default function ArticlesPage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'sv';
  const isRTL = lang === 'ar' || lang === 'fa';
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const t = (obj) => obj[lang] || obj.en;

  const [selectedArticle, setSelectedArticle] = React.useState(null);

  if (selectedArticle) {
    const a = selectedArticle;
    const content = a.content[lang] || a.content.en;
    const Icon = a.icon;
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-[#0a0a0b]" dir={isRTL ? 'rtl' : 'ltr'}>
        <SEO page="articles" />
        <Navbar />
        <VisibleBreadcrumbs />
        <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="article-detail">
          <button
            onClick={() => setSelectedArticle(null)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-all mb-6"
            data-testid="back-to-articles"
          >
            {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
            {t(pageTexts.backToArticles)}
          </button>

          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${a.color}`}>
            <Icon className="w-6 h-6" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
            {t(a.title)}
          </h1>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-8">
            <Clock className="w-4 h-4" />
            <span>{a.readTime} {t(pageTexts.readTime)}</span>
          </div>

          <div className="space-y-6">
            {content.map((section, idx) => (
              <div key={idx}>
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-2">{section.heading}</h2>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{section.text}</p>
              </div>
            ))}
          </div>
        </main>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 w-full"><RelatedPages currentPage="articles" /></div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-[#0a0a0b]" dir={isRTL ? 'rtl' : 'ltr'}>
      <SEO page="articles" />
      <Navbar />
      <VisibleBreadcrumbs />
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="articles-page">
        <div className="text-center mb-10">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <BookOpen className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-2">{t(pageTexts.pageTitle)}</h1>
          <p className="text-slate-500 dark:text-slate-400">{t(pageTexts.pageSubtitle)}</p>
        </div>

        <div className="grid gap-4 sm:gap-6">
          {articles.map((article) => {
            const Icon = article.icon;
            return (
              <button
                key={article.id}
                onClick={() => setSelectedArticle(article)}
                className="group flex flex-col sm:flex-row items-start gap-4 p-5 sm:p-6 bg-white dark:bg-[#18181b] border border-slate-200 dark:border-[#27272a] rounded-2xl hover:border-emerald-400 dark:hover:border-emerald-600 hover:shadow-md transition-all text-start"
                data-testid={`article-${article.id}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${article.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors mb-1">
                    {t(article.title)}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mb-2">
                    {t(article.summary)}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {article.readTime} {t(pageTexts.readTime)}</span>
                    <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                      {t(pageTexts.readMore)} <Arrow className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 w-full"><RelatedPages currentPage="articles" /></div>
      <Footer />
    </div>
  );
}

export { articlePaths };
